class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close(evt));
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    window.clearTimeout(this.closeTimer);
    window.clearTimeout(this.openTimer);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    const drawerInner = this.querySelector('.drawer__inner');
    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : document.getElementById('CartDrawer');
    const focusElement = drawerInner || this.querySelector('.drawer__close');
    let focusIsTrapped = false;

    if (this.openTransitionHandler) {
      drawerInner?.removeEventListener('transitionend', this.openTransitionHandler);
    }

    const trapDrawerFocus = () => {
      if (focusIsTrapped || !this.classList.contains('active')) return;
      focusIsTrapped = true;
      drawerInner?.removeEventListener('transitionend', handleOpenTransitionEnd);
      this.openTransitionHandler = null;
      window.clearTimeout(this.openFocusTimer);
      trapFocus(containerToTrapFocusOn, focusElement);
    };

    const handleOpenTransitionEnd = (event) => {
      if (event.target !== drawerInner || event.propertyName !== 'transform') return;
      trapDrawerFocus();
    };

    this.openTransitionHandler = handleOpenTransitionEnd;
    drawerInner?.addEventListener('transitionend', handleOpenTransitionEnd);
    this.openFocusTimer = window.setTimeout(trapDrawerFocus, 500);

    this.classList.add('animate');
    this.openTimer = window.setTimeout(() => {
      this.classList.add('active');
      drawerInner?.setAttribute('aria-hidden', 'false');
    });

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close(event) {
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.openFocusTimer);
    const drawerInner = this.querySelector('.drawer__inner');

    if (this.openTransitionHandler) {
      drawerInner?.removeEventListener('transitionend', this.openTransitionHandler);
      this.openTransitionHandler = null;
    }

    this.classList.remove('active');
    drawerInner?.setAttribute('aria-hidden', 'true');

    const shouldKeepReturnedFocus = !event || event.type.startsWith('key') || event.detail === 0;
    const focusTarget = this.activeElement?.isConnected
      ? this.activeElement
      : document.querySelector('#cart-icon-bubble');
    removeTrapFocus(focusTarget);

    if (!shouldKeepReturnedFocus && focusTarget) {
      focusTarget.blur();
      focusTarget.classList.remove('focused');
    }

    document.body.classList.remove('overflow-hidden');

    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => {
      if (!this.classList.contains('active')) this.classList.remove('animate');
    }, 500);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);